import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from '~/components/ConfirmDialog'
import { SidePanel } from '~/components/SidePanel'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { userDisplayName } from '~/helpers/users/display-name'
import { formatScore } from '../score-format'
import ScoreCorrectionForm from './ScoreCorrectionForm'
import ScoreCorrectionHistory from './ScoreCorrectionHistory'
import { useScoreCorrection } from './use-score-correction'

/** La ligne de statistiques corrigée : un apprenant sur un module de parcours. */
export type CorrectedRow = {
  id: string
  userEmail?: string | null
  userFirstName?: string | null
  userLastName?: string | null
  sessionName: string
  scoreRaw?: number | null
  scoreMax?: number | null
  correctedScore?: number | null
}

type Props = { row: CorrectedRow | null, onClose: () => void, onChanged: () => unknown }

/**
 * Corriger le score d'un apprenant (#161) : le score en vigueur, l'annulation
 * d'une correction, une nouvelle saisie et l'historique complet.
 */
function ScoreCorrectionPanel({ row, onClose, onChanged }: Props) {
  const { t } = useTranslation()
  const correction = useScoreCorrection(row?.id ?? null, onChanged)
  const [confirmingRevoke, setConfirmingRevoke] = useState(false)
  const name = row ? userDisplayName({ firstName: row.userFirstName, lastName: row.userLastName, email: row.userEmail }) : ''

  return (
    <SidePanel open={row != null} onOpenChange={open => !open && onClose()} title={t('scoreCorrection.title')} description={row ? `${name} — ${row.sessionName}` : undefined}>
      {row && (
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-2" aria-labelledby="score-current">
            <h3 id="score-current" className="text-sm font-semibold">{t('scoreCorrection.current')}</h3>
            <p className="text-sm">{formatScore(row.scoreRaw, row.scoreMax) ?? '—'}</p>
            {row.correctedScore != null && (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">{t('scoreCorrection.inForce')}</p>
                <Button type="button" variant="outline" className="w-fit" disabled={correction.busy} onClick={() => setConfirmingRevoke(true)}>
                  {t('scoreCorrection.revoke')}
                </Button>
              </div>
            )}
          </section>
          <section className="flex flex-col gap-2" aria-labelledby="score-new">
            <h3 id="score-new" className="text-sm font-semibold">{t('scoreCorrection.new')}</h3>
            <ScoreCorrectionForm busy={correction.busy} onSubmit={correction.correct} />
          </section>
          <section className="flex flex-col gap-2" aria-labelledby="score-history" aria-busy={correction.loading}>
            <h3 id="score-history" className="text-sm font-semibold">{t('scoreCorrection.history')}</h3>
            {correction.loading
              ? <Spinner className="size-5" />
              : correction.error
                ? <p role="alert" className="text-sm text-destructive">{t('scoreCorrection.loadError')}</p>
                : <ScoreCorrectionHistory history={correction.history} />}
          </section>
          <ConfirmDialog
            open={confirmingRevoke}
            title={t('scoreCorrection.revokeTitle')}
            confirmLabel={t('scoreCorrection.revoke')}
            onCancel={() => setConfirmingRevoke(false)}
            onConfirm={() => {
              setConfirmingRevoke(false)
              void correction.revoke()
            }}
          >
            {t('scoreCorrection.revokeBody')}
          </ConfirmDialog>
        </div>
      )}
    </SidePanel>
  )
}

export default ScoreCorrectionPanel
